import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { rejectParticipantAudio, rejectParticipantVideo } from '../../../av-moderation/actions';
import participantsPaneTheme from '../../../base/components/themes/participantsPaneTheme.json';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { getRaiseHandsQueue, isLocalParticipantModerator } from '../../../base/participants/functions';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import useContextMenu from '../../../base/ui/hooks/useContextMenu.web';
import { normalizeAccents } from '../../../base/util/strings.web';
import { getBreakoutRooms, getCurrentRoomId, isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { isButtonEnabled, showOverflowDrawer } from '../../../toolbox/functions.web';
import { muteRemote } from '../../../video-menu/actions.web';
import { isCurrentRoomRenamable, shouldRenderInviteButton } from '../../functions';
import { useParticipantDrawer } from '../../hooks.web';
import MeetingParticipantContextMenu from './MeetingParticipantContextMenu';
import MeetingParticipantItems from './MeetingParticipantItems';
import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown, IconArrowUp } from '../../../base/icons/svg';
import { changeDisplayRaisedHand } from '../../actions.any';
import { getKnockingParticipants } from '../../../lobby/functions';
import { countCascadeRaiseHands, getCascadeRaisedHandQueue } from '../../../raisehand-moderation/functions';
import CascadeRaisedHandRoomItem from './CascadeRaisedHandRoomItem';
const useStyles = makeStyles()(theme => {
    return {
        headingW: {
            color: theme.palette.warning02
        },
        heading: {
            color: theme.palette.text02,
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            marginBottom: theme.spacing(3),
            [`@media(max-width: ${participantsPaneTheme.MD_BREAKPOINT})`]: {
                ...withPixelLineHeight(theme.typography.bodyShortBoldLarge)
            }
        },
        search: {
            margin: `0 0 ${theme.spacing(3)} 0`,
            '& input': {
                // textAlign: 'center',
                paddingRight: '16px'
            },
            position: 'sticky',
            top: '10px',
            paddingRight: '24px',
            "& svg": {
                fill: "#73777A !important"
            }
        },
        participant: {
            color: theme.palette.default00,
            fontSize: '16px',
            fontWeight: 700,
        }
    };
});
/**
 * Renders the MeetingParticipantList component.
 * NOTE: This component is not using useSelector on purpose. The child components MeetingParticipantItem
 * and MeetingParticipantContextMenu are using connect. Having those mixed leads to problems.
 * When this one was using useSelector and the other two were not -the other two were re-rendered before this one was
 * re-rendered, so when participant is leaving, we first re-render the item and menu components,
 * throwing errors (closing the page) before removing those components for the participant that left.
 *
 * @returns {ReactNode} - The component.
 */
function RaisedHandParticipants({ currentRoom, overflowDrawer, participantsCount, searchString, setSearchString, showInviteButton, sortedParticipantIds = [], cascadeRaisedHandsQueue = [], countAllCascadeRaisedHandQueue, leftParentElRef }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const participants = useSelector(getKnockingParticipants);
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const { tabLeftParticipant, isDisplayParticipated, isDisplayLobby, isDisplayRaisedHand } = useSelector((state) => state['features/participants-pane']);
    const [lowerMenu, , toggleMenu, menuEnter, menuLeave, raiseContext] = useContextMenu();
    const muteAudio = useCallback(id => () => {
        dispatch(muteRemote(id, MEDIA_TYPE.AUDIO));
        dispatch(rejectParticipantAudio(id));
    }, [dispatch]);
    const stopVideo = useCallback(id => () => {
        dispatch(muteRemote(id, MEDIA_TYPE.VIDEO));
        dispatch(rejectParticipantVideo(id));
    }, [dispatch]);
    const [drawerParticipant, closeDrawer, openDrawerForParticipant] = useParticipantDrawer();
    // FIXME:
    // It seems that useTranslation is not very scalable. Unmount 500 components that have the useTranslation hook is
    // taking more than 10s. To workaround the issue we need to pass the texts as props. This is temporary and dirty
    // solution!!!
    // One potential proper fix would be to use react-window component in order to lower the number of components
    // mounted.
    const participantActionEllipsisLabel = t('participantsPane.actions.moreParticipantOptions');
    const youText = t('chat.you');
    const isBreakoutRoom = useSelector(isInBreakoutRoom);
    const _isCurrentRoomRenamable = useSelector(isCurrentRoomRenamable);
    const { classes: styles } = useStyles();
    const closeParticipane = () => {
        dispatch(changeDisplayRaisedHand(false));
    };
    const openParticipane = () => {
        dispatch(changeDisplayRaisedHand(true));
    };
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: 'center', height: (sortedParticipantIds?.length + countAllCascadeRaisedHandQueue) > 0 ? '48px' : '0px' } },
            (sortedParticipantIds?.length + countAllCascadeRaisedHandQueue > 0) ? (React.createElement("div", { className: styles.participant }, t("participantsPane.headings.raisedHand", { count: sortedParticipantIds.length + countAllCascadeRaisedHandQueue }))) : null,
            sortedParticipantIds?.length + countAllCascadeRaisedHandQueue > 0 ? (isDisplayRaisedHand ? (React.createElement(Icon, { onClick: () => closeParticipane(), style: { cursor: "pointer", marginRight: '8px' }, src: IconArrowUp, size: 18, color: "#44494D" })) : (React.createElement(Icon, { onClick: () => openParticipane(), style: { cursor: "pointer", marginRight: '8px' }, src: IconArrowDown, size: 18, color: "#44494D" }))) : null),
        isDisplayRaisedHand ? (React.createElement("div", { style: { marginBottom: '12px' } },
            React.createElement("div", null,
                React.createElement(MeetingParticipantItems, { isInBreakoutRoom: isBreakoutRoom, lowerMenu: lowerMenu, muteAudio: muteAudio, openDrawerForParticipant: openDrawerForParticipant, overflowDrawer: false, participantActionEllipsisLabel: participantActionEllipsisLabel, participantIds: sortedParticipantIds, raiseContextId: raiseContext.entity, searchString: normalizeAccents(searchString), stopVideo: stopVideo, toggleMenu: toggleMenu, youText: youText, isRaisedHand: true, renderAll: true, leftParentElRef: leftParentElRef, handleClick: handleClick })),
            cascadeRaisedHandsQueue.map((c) => (React.createElement(CascadeRaisedHandRoomItem, { id: c.originRoomId, isDirect: c.originRoomId === c.forwardRoomId, participantID: c.participantId, roomId: c.originRoomId, roomName: c.originCascadeName, forwardRoomName: c.forwardRoomName, users: c.users, lowerMenu: lowerMenu, toggleMenu: toggleMenu, raiseContextId: raiseContext.entity, searchString: normalizeAccents(searchString), openDrawerForParticipant: openDrawerForParticipant, muteAudio: muteAudio, stopVideo: stopVideo, overflowDrawer: false, isRaisedHand: true }))),
            React.createElement(MeetingParticipantContextMenu, { closeDrawer: closeDrawer, drawerParticipant: drawerParticipant, muteAudio: muteAudio, offsetTarget: raiseContext?.offsetTarget, onEnter: menuEnter, onLeave: menuLeave, onSelect: lowerMenu, overflowDrawer: false, participantID: raiseContext?.entity, anchorEl: anchorEl, open: open, handleClose: handleClose, useMuiMenu: true }))) : null));
}
/**
 * Maps (parts of) the redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state) {
    let sortedParticipantIds = getRaiseHandsQueue(state).map(({ id: particId }) => particId);
    const cascadeRaisedHandsQueue = getCascadeRaisedHandQueue(state);
    const countAllCascadeRaisedHandQueue = countCascadeRaiseHands(state) || 0;
    const participantsCount = sortedParticipantIds.length;
    const showInviteButton = shouldRenderInviteButton(state) && isButtonEnabled('invite', state);
    const overflowDrawer = showOverflowDrawer(state);
    const currentRoomId = getCurrentRoomId(state);
    const currentRoom = getBreakoutRooms(state)[currentRoomId];
    const isLocalModerator = isLocalParticipantModerator(state);
    return {
        currentRoom,
        overflowDrawer,
        participantsCount,
        showInviteButton,
        sortedParticipantIds,
        cascadeRaisedHandsQueue,
        countAllCascadeRaisedHandQueue
    };
}
export default connect(_mapStateToProps)(RaisedHandParticipants);
